import type { Supplier } from '~/types'
import { useLogger } from '#layers/base/composables/useLogger'

export const useSupplier = defineStore('supplier', () => {
  const log = useLogger('useSupplier')
  const { baseURL } = useRuntimeConfig().app
  const userStore = useUserStore()
  const featureSettingsStore = useFeatureSettingsStore()

  const accessToken = computed(() => userStore.accessToken)
  const headerFields = computed(() => ({ Authorization: `Bearer ${unref(accessToken)}` }))
  const abortIfNotAuthenticated = useAbortIfNotAuthenticated()

  const {
    data: suppliersData,
    pending: isSupplierDataPending,
    refresh: reloadSupplierData,
  } = useFetch<Supplier[]>('/api/intern/suppliers', {
    headers: headerFields,
    baseURL,
    ...abortIfNotAuthenticated,
    onResponseError({ response }) {
      log.warn('Failed to fetch suppliers', { responseStatus: response.status })
      if (response.status === 401) userStore.isAuthenticated = false
    },
  })

  const suppliers = computed(() => {
    return suppliersData.value?.filter((s) => {
      return !featureSettingsStore.settings?.clinicChain[unref(userStore.gpoHandle)]?.exclude?.suppliers?.includes(s.handle)
    }).map(s => ({
      ...s,
      freightAllowance: !Number.isNaN(Number(s.freightAllowance)) ? Number(s.freightAllowance) : s.freightAllowance.amount,
      freightCharge: !Number.isNaN(Number(s.freightCharge)) ? Number(s.freightCharge) : s.freightCharge.amount,
      discountNewCustomer: s.discountNewCustomer,
      isVatIdRequired: s.isVatIdRequired,
      possibleServiceItems: s.possibleServiceItems ?? s.offeredServiceItems.map(item => item.handle),
      imageUrl: s.imageUrl ?? s.image,
      minCartPrice: s.minCartPrice,
      locale: '',
    })) ?? []
  })

  function sendProductNotFoundEmail(supplierHandle: string, feedbackText: string) {
    (async function () {
      const { $snackbar, $i18n } = useNuxtApp()
      try {
        await $fetch('/api/v2/emails/product-feedback', {
          method: 'POST',
          body: { supplierHandle, feedbackText },
          baseURL,
          onResponseError({ response }) {
            if (response.status === 401) userStore.isAuthenticated = false
          },
        })
        $snackbar.showMessage({ text: $i18n.t('searchResult.productNotFoundDialog.success'), color: 'success', closeText: $i18n.t('close') })
      }
      catch (error) {
        log.warn('Could not send product not found mail to supplier', { error })
      }
    })()
  }

  function $reset() {
    reloadSupplierData()
  }

  return {
    suppliersData,
    suppliers,
    isSupplierDataPending,
    reloadSupplierData,
    $reset,
    sendProductNotFoundEmail,
  }
})
